import React, { useMemo } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { CheckCircle, XCircle, Warning } from "@phosphor-icons/react";
import { validateField, RULES } from "@/lib/dataQuality";

/**
 * <ValidatedInput field="cedula" value={v} onChange={setV} onValidChange={(ok, normalized) => ...} />
 *
 * Fields: cedula, rnc, pasaporte, vin, phone, email.
 * Renders ✓ / ✗ live status and blocks submit via `onValidChange`.
 */
export default function ValidatedInput({
  field,
  value,
  onChange,
  onValidChange,
  required = true,
  label,
  placeholder,
  className = "",
  testId,
}) {
  const rule = RULES[field];
  const check = useMemo(() => validateField(field, value), [field, value]);
  React.useEffect(() => {
    onValidChange?.(check.valid, check.normalized);
  }, [check.valid, check.normalized]);

  const empty = !value || String(value).trim() === "";
  const showStatus = !empty;

  return (
    <div className={`space-y-1 ${className}`}>
      {label && (
        <Label className="text-xs uppercase tracking-wider text-muted-foreground">
          {label}
          {required && <span className="text-rose-500 ml-1">*</span>}
        </Label>
      )}
      <div className="relative">
        <Input
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder || rule?.example}
          data-testid={testId || `input-${field}`}
          className={`pr-9 font-mono text-sm ${
            showStatus && !check.valid ? "border-rose-500 focus:ring-rose-500" :
            showStatus && check.valid ? "border-emerald-500 focus:ring-emerald-500" :
            ""
          }`}
        />
        {showStatus && (
          <div className="absolute right-2.5 top-1/2 -translate-y-1/2">
            {check.valid
              ? <CheckCircle size={16} weight="fill" className="text-emerald-500" data-testid={`valid-${field}`} />
              : <XCircle size={16} weight="fill" className="text-rose-500" data-testid={`invalid-${field}`} />}
          </div>
        )}
      </div>
      {showStatus && !check.valid && (
        <div className="text-[11px] text-rose-500 flex items-start gap-1" data-testid={`error-${field}`}>
          <Warning size={11} weight="fill" className="mt-0.5 shrink-0" />
          <span>{check.error}</span>
        </div>
      )}
      {(empty || check.valid) && rule?.hint && (
        <div className="text-[10px] text-muted-foreground">
          {rule.hint}
        </div>
      )}
    </div>
  );
}
